import { makeAutoObservable, observable, reaction, runInAction } from 'mobx';

import type { BaseStore } from '~/stores/interfaces/BaseStore';
import { Pagination } from '~/stores/shared/Pagination';

import type { MembershipGateway } from '../api/MembershipGateway';
import { membershipCartMarker } from '../domain/cart';
import { MembershipsDisabledError } from '../domain/errors';
import { switchablePlans } from '../domain/manage';
import {
  type MembershipActivation,
  type MembershipNotice,
  toWelcome,
} from '../domain/purchase';
import type {
  Membership,
  MembershipCharge,
  MembershipChargesPage,
  MembershipPlan,
} from '../domain/types';
import {
  isUnsettledPurchase,
  type LoadState,
  type MembershipActivationState,
  type MembershipView,
  toMembershipView,
} from '../domain/view';
import type { MembershipMarkers } from './MembershipMarkers';

/** Payment-history page size. */
export const CHARGES_PAGE_SIZE = 10;

/** Interval between `/me` checks while a `pending` purchase activates. */
export const ACTIVATION_POLL_INTERVAL_MS = 2_000;

/** Number of checks: one minute at `ACTIVATION_POLL_INTERVAL_MS`. */
export const ACTIVATION_POLL_ATTEMPTS = 30;

/** Waiting for an overdue membership to turn `active` after a new card answered `pending`. */
export type MembershipRestorationState = 'polling' | 'stalled';

/** Which request failed, as reported through `onError`. */
export type MembershipRequest = 'me' | 'plans' | 'charges';

/** How a `pending` purchase ended while the page polled. */
export type MembershipActivationOutcome =
  | { kind: 'activated'; membership: Membership }
  | { kind: 'failed'; planCode: string }
  | { kind: 'timeout'; planCode: string };

/** Everything the store needs from the outside world. */
export interface MembershipStoreDeps {
  gateway: MembershipGateway;
  /** Courier zone of the current ETA; `null` while unknown or when the address has none. */
  getCourierZoneId: () => string | null;
  /** Whether the customer has selected a delivery address. */
  hasDeliveryAddress: () => boolean;
  /** An address is selected but its ETA has not arrived yet. */
  isEtaPending: () => boolean;
  /**
   * Called for every failed request except a 404 on `/me` and a club switched
   * off on the backend; activation polling failures are not reported.
   */
  onError: (error: unknown, request: MembershipRequest) => void;
  /** Purchase markers kept in session storage. */
  markers: MembershipMarkers;
  /** Called once when activation polling ends. */
  onActivationSettled: (outcome: MembershipActivationOutcome) => void;
  /** Current time in ms, for the age of a `pending_card`; `Date.now` when absent. */
  now?: () => number;
  /** Whether a customer is signed in; a guest only reads the zone's plans. Signed in when absent. */
  isAuthorized?: () => boolean;
}

const IDLE = { status: 'idle' } as const;
const LOADING = { status: 'loading' } as const;
const FAILED = { status: 'failed' } as const;
const GUEST_ME = { status: 'ready', data: null } as const;

/**
 * Membership data of the signed-in customer. Loads lazily, re-requests plans
 * when the courier zone changes, waits for a `pending` purchase by polling
 * `/me`, and drops every in-flight answer on `reset()`.
 */
export class MembershipStore implements BaseStore {
  me: LoadState<Membership | null> = IDLE;
  plans: LoadState<MembershipPlan[]> = IDLE;
  readonly charges: Pagination<MembershipCharge>;
  /** A `pending` purchase is being waited for. */
  activation: MembershipActivationState | null = null;
  /** Banner of the last purchase or member card request. */
  notice: MembershipNotice | null = null;
  /** A new card answered `pending` for an overdue membership; `/me` is polled until it is `active`. */
  restoration: MembershipRestorationState | null = null;
  /**
   * The backend answered `MEMBERSHIPS_DISABLED`: the club is switched off and
   * every club surface hides. Cleared by the next `/me` or `/plans` answer.
   */
  isDisabled = false;
  /**
   * `membershipCartMarker` of the last `/me` answer; the cart re-calculates
   * when it changes. Kept while `/me` reloads or fails, so a reload alone does
   * not re-price the cart. `null` until the first `/me` answer. `/me` is
   * loaded once per session by `ClubSession` (so a member's cart re-prices
   * once after it) and again on entering the club page or a club modal: a
   * membership cancelled in another tab or in the CMS shows only after a
   * reload or such an entry, and until then calculate itself is the source of
   * truth. Any action that changes
   * the membership (purchase, cancel, plan switch, card change) must end in
   * `loadMe()`, `refresh()` or `applyMembership()`, otherwise the cart keeps
   * its old prices.
   */
  cartMarker: string | null = null;

  private plansZoneId: string | null = null;
  private isGuestLoaded = false;
  private meRequest = 0;
  private plansRequest = 0;
  private chargesEpoch = 0;
  private activationEpoch = 0;
  private activationTimer: ReturnType<typeof setTimeout> | null = null;
  private restorationEpoch = 0;
  private restorationTimer: ReturnType<typeof setTimeout> | null = null;
  /** Checks of the restoration wait already scheduled; a resumed wait goes on from here, not from zero. */
  private restorationAttempt = 0;
  /** Bumped by `close()`, so a page entry that outlives its page stops short. */
  private visit = 0;
  /** The current notice has been on screen; `close()` drops it. */
  private isNoticeSeen = false;
  private pending: Promise<void> | null = null;
  private disposeZoneReaction: () => void = () => undefined;

  constructor(private readonly deps: MembershipStoreDeps) {
    this.charges = new Pagination<MembershipCharge>((page) =>
      this.fetchCharges(page),
    );

    makeAutoObservable<
      MembershipStore,
      | 'deps'
      | 'pending'
      | 'disposeZoneReaction'
      | 'isGuestLoaded'
      | 'activationEpoch'
      | 'activationTimer'
      | 'restorationEpoch'
      | 'restorationTimer'
      | 'restorationAttempt'
      | 'visit'
      | 'isNoticeSeen'
    >(
      this,
      {
        me: observable.ref,
        plans: observable.ref,
        notice: observable.ref,
        deps: false,
        charges: false,
        pending: false,
        disposeZoneReaction: false,
        isGuestLoaded: false,
        activationEpoch: false,
        activationTimer: false,
        restorationEpoch: false,
        restorationTimer: false,
        restorationAttempt: false,
        visit: false,
        isNoticeSeen: false,
      },
      { autoBind: true },
    );

    this.disposeZoneReaction = reaction(
      () => this.zoneId,
      () => {
        if (this.me.status !== 'idle' || this.isGuestLoaded) {
          void this.loadPlans();
        }
      },
    );
  }

  /** Courier zone of the selected address; unknown without an address. */
  get zoneId(): string | null {
    return this.deps.hasDeliveryAddress() ? this.deps.getCourierZoneId() : null;
  }

  get view(): MembershipView {
    return this.viewWith(this.activation);
  }

  /**
   * The state Join resolves from. A wait that stalled has lost its
   * marker, so it counts as none here, exactly as it does after a reload;
   * only a wait still polling replaces the form.
   */
  get purchaseView(): MembershipView {
    return this.viewWith(this.activation === 'polling' ? 'polling' : null);
  }

  /**
   * Plans of the current zone the member can switch to; empty without a
   * membership or while the zone's plans are not loaded.
   */
  get switchablePlans(): MembershipPlan[] {
    const { view } = this;
    if (
      view.kind !== 'member' ||
      this.plansZoneId !== this.zoneId ||
      this.plans.status !== 'ready'
    ) {
      return [];
    }
    return switchablePlans(view.membership, this.plans.data);
  }

  /** Loads once; a later call joins the running load or returns at once. */
  ensureLoaded(): Promise<void> {
    if (this.me.status !== 'idle') {
      return this.pending ?? Promise.resolve();
    }
    return this.load();
  }

  /**
   * A guest has no `/me`: loads the zone's plans once, which the offer is
   * built from, and keeps them in step with the zone. `reset()` forgets it.
   */
  ensureGuestLoaded(): Promise<void> {
    if (this.isGuestLoaded) {
      return this.pending ?? Promise.resolve();
    }
    this.isGuestLoaded = true;
    const pending = this.loadPlans();
    this.pending = pending;
    void pending.finally(() => {
      if (this.pending === pending) {
        this.pending = null;
      }
    });
    return pending;
  }

  /**
   * Page entry: shows the welcome of a purchase made just before, re-requests
   * the membership and plans (or joins a load already running), resumes
   * waiting for a `pending` purchase, then loads the first charges page of a
   * member unless `withCharges` is `false` (the "Add card" sheet shows none). A
   * `pending_card` already known is shown as "Activating…" at once, not as
   * the offer it would read as until the load ends.
   */
  async open({
    withCharges = true,
  }: {
    withCharges?: boolean;
  } = {}): Promise<void> {
    const visit = this.visit;
    this.takeWelcome();
    this.syncActivation();
    this.syncRestoration();
    await (this.pending ?? this.load());
    if (visit !== this.visit) {
      return;
    }
    this.syncActivation();
    if (withCharges && this.view.kind === 'member') {
      await this.loadCharges();
    }
  }

  /**
   * The membership changed on the backend (a purchase settled): forgets the
   * known `/me`, so no screen renders from it, and re-reads the page data. A
   * page entry that follows joins this load instead of sending its own.
   */
  refresh(): Promise<void> {
    this.me = LOADING;
    return this.load();
  }

  /**
   * Shows a membership a member card request answered with: in place,
   * without the skeleton, dropping an older `/me` still in flight, with an
   * optional banner. The payment history is re-read, since an upgrade adds a
   * charge to it.
   */
  applyMembership(
    membership: Membership | null,
    notice?: MembershipNotice,
  ): void {
    this.acceptMe(membership);
    if (notice) {
      this.showNotice(notice);
    }
    if (membership) {
      void this.loadCharges();
    }
  }

  /**
   * Takes a `/me` answer that did not come from `loadMe` (an operation reply or
   * a polling check): makes any earlier page-entry `/me` stale and updates the
   * cart marker, so the cart re-prices after a cancel, a plan switch or a card
   * change.
   */
  private acceptMe(membership: Membership | null): void {
    this.meRequest += 1;
    this.me = { status: 'ready', data: membership };
    this.cartMarker = membershipCartMarker(membership);
  }

  /** Shows a closable banner; it is dropped on leaving the page once it has been seen. */
  showNotice(notice: MembershipNotice): void {
    this.notice = notice;
    this.isNoticeSeen = false;
  }

  /**
   * A new card answered `pending` for an overdue membership: polls `/me`
   * every 2 s for up to a minute until it is `active`. Survives leaving the
   * "Add card" sheet (`?modal=club-card`) — the membership page resumes it on entry with the checks it
   * has left, so leaving and coming back never stretches the minute.
   */
  awaitRestoration(): void {
    this.stopRestoration();
    this.restoration = 'polling';
    this.scheduleRestorationCheck(this.restorationEpoch, 0);
  }

  load(): Promise<void> {
    const pending = Promise.all([this.loadMe(), this.loadPlans()]).then(
      () => undefined,
    );
    this.pending = pending;
    void pending.finally(() => {
      if (this.pending === pending) {
        this.pending = null;
      }
    });
    return pending;
  }

  /**
   * Reads `/me`. With `keepOnFailure` a failed read leaves a membership
   * already on screen in place instead of the error screen; the failure is
   * still reported.
   */
  async loadMe({ keepOnFailure = false } = {}): Promise<void> {
    const request = ++this.meRequest;
    if (this.me.status !== 'ready') {
      this.me = LOADING;
    }
    try {
      const membership = await this.deps.gateway.getMe();
      runInAction(() => {
        if (request === this.meRequest) {
          this.me = { status: 'ready', data: membership };
          this.cartMarker = membershipCartMarker(membership);
          this.isDisabled = false;
        }
      });
    } catch (error) {
      if (request !== this.meRequest) {
        return;
      }
      if (error instanceof MembershipsDisabledError) {
        runInAction(() => {
          this.me = { status: 'ready', data: null };
          this.cartMarker = membershipCartMarker(null);
          this.isDisabled = true;
        });
        return;
      }
      runInAction(() => {
        if (!keepOnFailure || this.me.status !== 'ready') {
          this.me = FAILED;
        }
      });
      this.deps.onError(error, 'me');
    }
  }

  async loadPlans(): Promise<void> {
    const request = ++this.plansRequest;
    const zoneId = this.zoneId;
    if (zoneId === null) {
      this.plans = IDLE;
      this.plansZoneId = null;
      return;
    }
    if (this.plans.status !== 'ready' || this.plansZoneId !== zoneId) {
      this.plans = LOADING;
    }
    this.plansZoneId = zoneId;
    try {
      const plans = await this.deps.gateway.getPlans(zoneId);
      runInAction(() => {
        if (request === this.plansRequest) {
          this.plans = { status: 'ready', data: plans };
          this.isDisabled = false;
        }
      });
    } catch (error) {
      if (request !== this.plansRequest) {
        return;
      }
      if (error instanceof MembershipsDisabledError) {
        runInAction(() => {
          this.plans = { status: 'ready', data: [] };
          this.isDisabled = true;
        });
        return;
      }
      runInAction(() => {
        this.plans = FAILED;
      });
      this.deps.onError(error, 'plans');
    }
  }

  /**
   * Re-reads `/me` in place after a saved card, then the payment history,
   * since a restored overdue membership adds the renewal charge to it. A
   * failed `/me` keeps the membership on screen.
   */
  async reloadAfterCardChange(): Promise<void> {
    await this.loadMe({ keepOnFailure: true });
    if (this.view.kind === 'member') {
      await this.loadCharges();
    }
  }

  async loadCharges(): Promise<void> {
    try {
      await this.charges.loadPage(1);
    } catch (error) {
      this.chargesFailed(error);
    }
  }

  async loadMoreCharges(): Promise<void> {
    try {
      await this.charges.loadMore();
    } catch (error) {
      this.chargesFailed(error);
    }
  }

  /** A club switched off on the backend hides the club; any other failure is reported. */
  private chargesFailed(error: unknown): void {
    if (error instanceof MembershipsDisabledError) {
      runInAction(() => {
        this.isDisabled = true;
      });
      return;
    }
    this.deps.onError(error, 'charges');
  }

  dismissNotice(): void {
    this.notice = null;
  }

  /** The banner is on screen; from now on leaving the page drops it. */
  markNoticeSeen(): void {
    this.isNoticeSeen = this.notice !== null;
  }

  /**
   * The club page or the Join modal unmounted: stops polling `/me`, forgets a
   * stalled wait and drops a banner already shown. The activation marker is
   * kept, so the next page entry resumes the wait while it is still valid.
   */
  close(): void {
    this.visit += 1;
    this.stopActivation();
    this.activation = null;
    this.stopRestoration();
    if (this.restoration === 'stalled') {
      this.restoration = null;
    }
    if (this.isNoticeSeen) {
      this.notice = null;
      this.isNoticeSeen = false;
    }
  }

  /** "Refresh" after the wait stalled: forgets it and re-reads the page. */
  refreshActivation(): Promise<void> {
    this.stopActivation();
    this.activation = null;
    return this.open();
  }

  /** Forgets the customer's data and purchase markers; answers still in flight are discarded. */
  reset(): void {
    this.meRequest++;
    this.plansRequest++;
    this.chargesEpoch++;
    this.stopActivation();
    this.stopRestoration();
    this.pending = null;
    this.isGuestLoaded = false;
    this.me = IDLE;
    this.cartMarker = null;
    this.plans = IDLE;
    this.plansZoneId = null;
    this.activation = null;
    this.restoration = null;
    this.isDisabled = false;
    this.notice = null;
    this.isNoticeSeen = false;
    this.charges.reset();
    this.charges.setStatus('idle');
    this.deps.markers.clearAll();
  }

  destroy(): void {
    this.disposeZoneReaction();
    this.stopActivation();
    this.stopRestoration();
  }

  private viewWith(
    activation: MembershipActivationState | null,
  ): MembershipView {
    const isGuest = this.deps.isAuthorized?.() === false;
    return toMembershipView({
      me: isGuest ? GUEST_ME : this.me,
      plans: this.plansZoneId === this.zoneId ? this.plans : LOADING,
      hasDeliveryAddress: this.deps.hasDeliveryAddress(),
      isEtaPending: this.deps.isEtaPending(),
      zoneId: this.zoneId,
      activation,
      isDisabled: this.isDisabled,
      now: this.now(),
    });
  }

  private now(): number {
    return this.deps.now?.() ?? Date.now();
  }

  private takeWelcome(): void {
    const welcome = this.deps.markers.takeWelcome();
    if (welcome) {
      this.showNotice({ kind: 'welcome', welcome });
    }
  }

  /**
   * Starts waiting for a purchase that may still be charging: a `pending`
   * purchase of this customer (its marker) meeting `pending_card` or a `/me`
   * that failed, so its outcome is still unknown, and a `pending_card` without
   * a marker that is not yet abandoned (`isUnsettledPurchase`), started in
   * another tab or before the marker was lost. Drops a marker that no longer
   * applies, and stops a wait without a marker once a fresh `/me` shows its
   * `pending_card` abandoned.
   */
  private syncActivation(): void {
    const { me } = this;
    if (me.status === 'idle' || me.status === 'loading') {
      return;
    }
    const marker = this.deps.markers.readActivation();
    if (this.activation === 'polling') {
      if (
        !marker &&
        me.status === 'ready' &&
        !isUnsettledPurchase(me.data, this.now())
      ) {
        this.stopActivation();
        this.activation = null;
      }
      return;
    }
    if (me.status === 'failed') {
      if (marker) {
        this.startActivation(marker);
      }
      return;
    }
    const membership = me.data;
    if (membership?.status === 'pending_card') {
      if (marker) {
        this.startActivation(marker);
      } else if (isUnsettledPurchase(membership, this.now())) {
        this.startActivation({
          planId: membership.plan.id,
          planCode: membership.plan.code,
        });
      }
      return;
    }
    if (marker) {
      this.deps.markers.clearActivation();
    }
  }

  private startActivation(activation: MembershipActivation): void {
    this.stopActivation();
    this.activation = 'polling';
    this.scheduleActivationCheck(activation, this.activationEpoch, 0);
  }

  private stopActivation(): void {
    this.activationEpoch += 1;
    if (this.activationTimer !== null) {
      clearTimeout(this.activationTimer);
      this.activationTimer = null;
    }
  }

  private scheduleActivationCheck(
    marker: MembershipActivation,
    epoch: number,
    attempt: number,
  ): void {
    this.activationTimer = setTimeout(() => {
      void this.checkActivation(marker, epoch, attempt);
    }, ACTIVATION_POLL_INTERVAL_MS);
  }

  private async checkActivation(
    marker: MembershipActivation,
    epoch: number,
    attempt: number,
  ): Promise<void> {
    let membership: Membership | null | undefined;
    try {
      membership = await this.deps.gateway.getMe();
    } catch {
      membership = undefined;
    }
    if (epoch !== this.activationEpoch) {
      return;
    }
    this.applyActivationCheck(marker, epoch, attempt, membership);
  }

  /** One `/me` answer while waiting; `undefined` is a failed request, which keeps the wait going. */
  private applyActivationCheck(
    marker: MembershipActivation,
    epoch: number,
    attempt: number,
    membership: Membership | null | undefined,
  ): void {
    this.activationTimer = null;
    if (membership !== undefined) {
      this.acceptMe(membership);
    }
    const status = membership?.status;
    if (membership === null || status === 'expired') {
      this.endActivation(null);
      this.showNotice({ kind: 'declined' });
      this.deps.onActivationSettled({
        kind: 'failed',
        planCode: marker.planCode,
      });
      return;
    }
    if (membership && status !== 'pending_card') {
      this.endActivation(null);
      if (status === 'trialing' || status === 'active') {
        this.showNotice({
          kind: 'welcome',
          welcome: toWelcome(status, membership, membership.plan),
        });
      }
      this.deps.onActivationSettled({ kind: 'activated', membership });
      void this.loadCharges();
      return;
    }
    if (attempt + 1 >= ACTIVATION_POLL_ATTEMPTS) {
      this.endActivation('stalled');
      this.deps.onActivationSettled({
        kind: 'timeout',
        planCode: marker.planCode,
      });
      return;
    }
    this.scheduleActivationCheck(marker, epoch, attempt + 1);
  }

  private endActivation(next: 'stalled' | null): void {
    this.activation = next;
    this.deps.markers.clearActivation();
  }

  /** Resumes a restoration wait that a page change interrupted, with the checks it has left. */
  private syncRestoration(): void {
    if (this.restoration === 'polling' && this.restorationTimer === null) {
      this.scheduleRestorationCheck(
        this.restorationEpoch,
        this.restorationAttempt,
      );
    }
  }

  private stopRestoration(): void {
    this.restorationEpoch += 1;
    if (this.restorationTimer !== null) {
      clearTimeout(this.restorationTimer);
      this.restorationTimer = null;
    }
  }

  private scheduleRestorationCheck(epoch: number, attempt: number): void {
    this.restorationAttempt = attempt;
    this.restorationTimer = setTimeout(() => {
      void this.checkRestoration(epoch, attempt);
    }, ACTIVATION_POLL_INTERVAL_MS);
  }

  private async checkRestoration(
    epoch: number,
    attempt: number,
  ): Promise<void> {
    let membership: Membership | null | undefined;
    try {
      membership = await this.deps.gateway.getMe();
    } catch {
      membership = undefined;
    }
    if (epoch !== this.restorationEpoch) {
      return;
    }
    this.applyRestorationCheck(epoch, attempt, membership);
  }

  /** One `/me` answer while waiting; `undefined` is a failed request, which keeps the wait going. */
  private applyRestorationCheck(
    epoch: number,
    attempt: number,
    membership: Membership | null | undefined,
  ): void {
    this.restorationTimer = null;
    if (membership !== undefined) {
      this.acceptMe(membership);
    }
    if (membership !== undefined && membership?.status !== 'past_due') {
      this.restoration = null;
      if (membership?.status === 'active') {
        this.showNotice({
          kind: 'cardUpdated',
          restored: true,
          card: membership.card ?? null,
        });
        void this.loadCharges();
      }
      return;
    }
    if (attempt + 1 >= ACTIVATION_POLL_ATTEMPTS) {
      this.restoration = 'stalled';
      return;
    }
    this.scheduleRestorationCheck(epoch, attempt + 1);
  }

  private async fetchCharges(
    page: number,
  ): Promise<MembershipChargesPage | undefined> {
    const epoch = this.chargesEpoch;
    try {
      const result = await this.deps.gateway.getCharges({
        page,
        size: CHARGES_PAGE_SIZE,
      });
      return epoch === this.chargesEpoch ? result : undefined;
    } catch (error) {
      if (epoch !== this.chargesEpoch) {
        return undefined;
      }
      throw error;
    }
  }
}
